import { Component } from '@theme/component';
import { CollectionUpdateEvent, StandardEvents } from '@shopify/events';

const VIEW_PARAM = 'gp_sale_category';
const VIEW_ALL = 'all';
const VALID_VIEWS = new Set(['all', 'jocks', 'singlets', 'tanks-tees', 'swim', 'accessories', 'other']);

/**
 * Adds the sale collection's product-category views without replacing Fabric's
 * native filtering, sorting, pagination, or Section Rendering behavior.
 */
class SaleCollectionViewComponent extends Component {
  /** @type {string} */
  #view = VIEW_ALL;

  /** @type {MutationObserver | null} */
  #gridObserver = null;

  /** @type {WeakMap<Element, string>} */
  #nativeText = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.#view = this.#readViewFromURL();
    this.#captureNativeText();
    this.#observeGrid();
    this.#applyView();

    window.addEventListener('popstate', this.#handlePopState);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#gridObserver?.disconnect();
    this.#gridObserver = null;
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
  }

  /**
   * @param {Event} event
   */
  selectView(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;

    const view = event.target.dataset.view;
    if (!view || !VALID_VIEWS.has(view) || view === this.#view) return;

    this.#view = view;
    this.#syncURL('push');
    this.#applyView();
  }

  #readViewFromURL() {
    const view = new URL(window.location.href).searchParams.get(VIEW_PARAM) ?? VIEW_ALL;
    return VALID_VIEWS.has(view) ? view : VIEW_ALL;
  }

  /**
   * @param {'push' | 'replace'} mode
   */
  #syncURL(mode) {
    const url = new URL(window.location.href);

    if (this.#view === VIEW_ALL) {
      url.searchParams.delete(VIEW_PARAM);
    } else {
      url.searchParams.set(VIEW_PARAM, this.#view);
    }

    const method = mode === 'push' ? 'pushState' : 'replaceState';
    history[method](history.state, '', url.toString());
  }

  #handlePopState = () => {
    this.#view = this.#readViewFromURL();
    this.#applyView();
  };

  /**
   * @param {CollectionUpdateEvent} event
   */
  #handleCollectionUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    const mySection = this.closest('[id^="shopify-section-"]');
    if (eventSection && mySection && eventSection !== mySection) return;

    this.#syncURL('replace');
    event.promise.then(this.#refreshAfterSectionRender, this.#refreshAfterSectionRender);
  };

  #refreshAfterSectionRender = () => {
    if (!this.isConnected) return;

    this.#captureNativeText(true);
    this.#observeGrid();
    this.#applyView();
  };

  #getResultsList() {
    return this.closest('results-list');
  }

  #getGrid() {
    return this.#getResultsList()?.querySelector('[data-testid="product-grid"]') ?? null;
  }

  #observeGrid() {
    this.#gridObserver?.disconnect();
    this.#gridObserver = null;

    const grid = this.#getGrid();
    if (!grid) return;

    this.#gridObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList' && mutation.addedNodes.length > 0)) {
        this.#applyView();
      }
    });
    this.#gridObserver.observe(grid, { childList: true });
  }

  #applyView() {
    const grid = this.#getGrid();
    const cards = grid ? Array.from(grid.querySelectorAll(':scope > [data-gp-sale-category]')) : [];
    const availableViews = new Set(cards.map((card) => card.getAttribute('data-gp-sale-category')).filter(Boolean));

    const buttons = Array.isArray(this.refs.viewButtons) ? this.refs.viewButtons : [];
    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const view = button.dataset.view ?? VIEW_ALL;
      button.hidden = view !== VIEW_ALL && !availableViews.has(view);
    }

    if (this.#view !== VIEW_ALL && !availableViews.has(this.#view)) {
      this.#view = VIEW_ALL;
      this.#syncURL('replace');
    }

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.setAttribute('aria-pressed', String(button.dataset.view === this.#view));
    }

    if (!grid) {
      this.#updateNativeCounts(this.#view === VIEW_ALL ? null : 0);
      this.#syncPaginationLinks();
      return;
    }

    let visibleCount = 0;
    for (const card of cards) {
      const isVisible = this.#view === VIEW_ALL || card.getAttribute('data-gp-sale-category') === this.#view;
      card.toggleAttribute('hidden', !isVisible);
      if (isVisible) visibleCount += 1;
    }

    this.#updateNativeCounts(this.#view === VIEW_ALL ? null : visibleCount);
    this.#syncPaginationLinks();
  }

  /**
   * @param {boolean} force
   */
  #captureNativeText(force = false) {
    const resultsList = this.#getResultsList();
    if (!resultsList) return;

    const elements = resultsList.querySelectorAll('.products-count-wrapper [role="status"], .facets__see-results');
    for (const element of elements) {
      if (force || !this.#nativeText.has(element)) {
        this.#nativeText.set(element, element.textContent ?? '');
      }
    }
  }

  /**
   * @param {number | null} count
   */
  #updateNativeCounts(count) {
    const resultsList = this.#getResultsList();
    if (!resultsList) return;

    const countTemplate = this.dataset.countTemplate ?? '__COUNT__';
    const seeResultsTemplate = this.dataset.seeResultsTemplate ?? '__COUNT__';

    for (const element of resultsList.querySelectorAll('.products-count-wrapper [role="status"]')) {
      if (count === null) {
        const nativeText = this.#nativeText.get(element);
        if (nativeText !== undefined) element.textContent = nativeText;
      } else {
        element.textContent = countTemplate.replaceAll('__COUNT__', String(count));
      }
    }

    for (const element of resultsList.querySelectorAll('.facets__see-results')) {
      if (count === null) {
        const nativeText = this.#nativeText.get(element);
        if (nativeText !== undefined) element.textContent = nativeText;
      } else {
        element.textContent = seeResultsTemplate.replaceAll('__COUNT__', String(count));
      }
    }
  }

  #syncPaginationLinks() {
    const resultsList = this.#getResultsList();
    if (!resultsList) return;

    for (const link of resultsList.querySelectorAll('.pagination a[href]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const url = new URL(link.href, window.location.origin);
      if (this.#view === VIEW_ALL) {
        url.searchParams.delete(VIEW_PARAM);
      } else {
        url.searchParams.set(VIEW_PARAM, this.#view);
      }

      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    }
  }
}

if (!customElements.get('sale-collection-view-component')) {
  customElements.define('sale-collection-view-component', SaleCollectionViewComponent);
}
